import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { AppService } from '../app.service';
import { ApiService } from '../core/api';
import { ChartService } from '../shared/chart/chart.service';
import { DialogService } from '../shared/dialog/dialog.service';
import { PageInfoComponent } from '../shared/page-info/page-info.component';
import { CustomThemeService } from '../theme/core/custom-theme.service';
import { ThemeService } from '../theme/core/theme.service';
import { ThemeManageComponent } from '../theme/shared/theme-manage/theme-manage.component';
import { User } from '../user/core/user';
import { UserService } from '../user/core/user.service';
import { NavStateService } from './nav-state.service';

import { of, switchMap } from 'rxjs';
import { ConfigService } from '../core/config/config.service';
import { PermissionService } from '../permissions/core/permission.service';
import { ChatWindowComponent, canFocusOn } from '../shared/chat-window/chat-window.component';

@Component({
	selector: 'app-dash',
	templateUrl: 'master.component.html',
	styleUrls: ['master.component.css'],
	standalone: false
})
export class MasterComponent implements OnInit {
	readonly EMPTY_THEME_NAME = 'empty';

	@ViewChild('container') container;
	@ViewChild('container', { read: ElementRef }) containerEl: ElementRef<HTMLElement>;
	@ViewChild('navigation') navigation;
	@ViewChild(ChatWindowComponent) chatWindow?: ChatWindowComponent;

	navigationModeMini: boolean = false;
	themeUrl: SafeResourceUrl;
	currentTheme: string = this.EMPTY_THEME_NAME;
	me: User;
	navigationOpened: boolean = false;
	miniNavigationHovered: boolean = false;
	chatOpened: boolean = false;
	/** Whether the page is one the assistant can be asked about (an invoice, inventory item, account, or their lists). */
	canAskAboutPage: boolean = false;
	isAiEnabled: boolean = false;

	/**
	 * Whether the shell offers the AI assistant: to every logged-in user, where the instance has not opted out
	 * (application_info.is_ai_enabled). It was for internal employees only. What a user can actually ask is
	 * decided by the server, from their roles and the customer's package (MCP/Util/Modules).
	 */
	get showsAssistant(): boolean {
		return this.isAiEnabled && !!this.me;
	}
	chatDockWidth: number = 0;
	chatDockTop: number = 64;
	onlineFlag = navigator.onLine;
	dashEnabled: boolean = true;
	isDashEdit: boolean;
	isReportPermissionAvailable = false;
	canUseFullMenu = false;

	public resultConfiguration: any;
	public support_url_config: string = 'https://connectbase.atlassian.net/wiki/spaces/b3f781f6910f458296a02d7fe1291279/pages/6451560453/InvoiceIQ+Help+Center';

	constructor(
		public sanitizer: DomSanitizer,
		public router: Router,
		public dialog: DialogService,
		public themeService: ThemeService,
		public chartService: ChartService,
		public appService: AppService,
		public customThemeService: CustomThemeService,
		public navStateService: NavStateService,
		public apiService: ApiService,
		public userService: UserService,
		public configService: ConfigService,
		private permissionService: PermissionService
	) {
		window.addEventListener('online', () => {
			this.onlineFlag = true;
		});
		window.addEventListener('offline', () => {
			this.onlineFlag = false;
		});

		this.appService.subscribe((data) => {
			if (data.type === 'user') {
				let user = data.object;
				if (user.id === this.me.id) {
					this.me = user;
				}
			}
		});
	}

	dashEdit() {
		this.isDashEdit = !this.isDashEdit;
		this.appService.emit('DASH:EDIT', {
			enabled: this.isDashEdit
		});
	}

	ngOnInit() {
		this.apiService.clearAllCache();

		if (this.currentTheme !== this.EMPTY_THEME_NAME) {
			this.themeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.buildUrl(this.currentTheme));
		}

		this.canAskAboutPage = canFocusOn(this.router.url ?? '');
		this.router.events.subscribe((val) => {
			if (val instanceof NavigationEnd) {
				this.canAskAboutPage = canFocusOn(val.urlAfterRedirects);
				this.isDashEdit = false;
				this.appService.emit('DASH:EDIT', {
					enabled: this.isDashEdit
				});
			}
		});

		this.configService.findAll().subscribe((config) => {
			this.isAiEnabled = config?.application_info?.is_ai_enabled !== false;
		});

		this.permissionService.reportPermission().subscribe((permission) => {
			this.isReportPermissionAvailable = !permission;
		});

		this.permissionService
			.isAdmin()
			.pipe(
				switchMap((value) => {
					return value ? of(!value) : this.permissionService.hasInvoiceIQPermissions();
				})
			)
			.subscribe((value) => {
				this.canUseFullMenu = !value;
			});

		this.userService.settings().subscribe((result) => {
			const selTheme = result[this.themeService.selectedKey];
			const selChart = result[this.chartService.selectedKey];

			ThemeService.setActiveTheme(this.themeService.getActiveTheme(selTheme, result));
			ChartService.setActiveChartTheme(this.chartService.getActiveChartTheme(selChart, result));

			this.customThemeService.generateCustomTheme(ThemeService.activeTheme, ChartService.activeChartTheme);
		});

		this.userService.me().subscribe((user: User) => {
			this.me = user;
			localStorage.setItem('date_format', user.country.date_format);
			localStorage.setItem('date_time_format', user.country.date_time_format);

			localStorage.setItem('__currencyDisplay', JSON.stringify(user.country.currency_display));
			localStorage.setItem('__currDigit', JSON.stringify(user.country.curr_num_digit));
			localStorage.setItem('__currLocale', JSON.stringify(user.country.curr_num_locale));
			localStorage.setItem('__negativeValue', JSON.stringify(user.country.negative_value));
			this.navigationOpened = true;
		});
	}

	public buildUrl(theme: string): string {
		return `assets/themes/${theme}.css`;
	}

	profile() {
		if (this.me) {
			/* Subscriber in SearchResultsService - GPX-2401 */
			this.appService.emit('USER:PROFILE', { user: this.me });
		}
	}

	tickets() {
		let supportTab = window.open('https://support.jira.connectbase.com/servicedesk/customer/portals', '_blank');
		// let supportTab = window.open('https://gpxcloud.freshdesk.com/support/tickets', '_blank');
		supportTab.focus();
	}

	support() {
		let supportTab = window.open(this.support_url_config, '_blank');
		supportTab.focus();
	}

	customize() {
		this.dialog
			.open(
				ThemeManageComponent,
				{
					theme: Object.assign({}, this.currentTheme)
				},
				{
					width: '1200px'
				}
			)
			.afterClosed()
			.subscribe((theme) => {
				if (!theme) {
					return;
				}

				this.refreshChartColors(location.pathname);

				this.currentTheme = Object.assign({}, theme);
			});
	}

	refreshChartColors(urlString: string) {
		const urlsWithCharts: Array<string> = ['/dash', '/srm', '/account', '/account/[0-9]+/show', '/report-gallery/[0-9]+/show'];

		const pageWillRefresh = urlsWithCharts.filter((item) => urlString.match(item)).length;

		if (pageWillRefresh) {
			location.reload();
		}
	}

	info() {
		this.dialog.open(PageInfoComponent, {}, { width: '400px' });
	}

	signOut() {
		localStorage.removeItem('__localeId');
		localStorage.removeItem('__countries');
		localStorage.removeItem('__currencyDisplay');
		localStorage.removeItem('__currDigit');
		localStorage.removeItem('__currLocale');
		localStorage.removeItem('__negativeValue');
		localStorage.removeItem('currency');
		localStorage.removeItem('__numberOfDigits');
		localStorage.removeItem('__currencies');
		this.router.navigate(['/auth/sign-out']);
	}

	toggleNavigation() {
		this.navigationModeMini = !this.navigationModeMini;
		this.miniNavigationHovered = false;
		this.navStateService.setNavState(!this.navigationModeMini);

		//this.navigationMode = this.navigationModeMini ? 'side' : 'over';
	}

	toggleChat() {
		this.chatOpened = !this.chatOpened;
		this.measureChatDockTop();
	}

	/** "Ask about this page": opens the assistant if it is closed, and starts it over about this page. */
	async askAboutPage() {
		if (!this.canAskAboutPage || !this.chatWindow) return;
		if (!this.chatOpened) this.toggleChat();
		await this.chatWindow.askAboutPage();
	}

	/** Room the page leaves on the right for the docked assistant; none while the chat is closed. */
	get chatPageInset(): number {
		return this.chatOpened ? this.chatDockWidth : 0;
	}

	onChatDockWidth(width: number) {
		this.chatDockWidth = width;
		this.measureChatDockTop();
	}

	// The docked assistant starts where the page container does, which moves with the theme's density.
	@HostListener('window:resize')
	measureChatDockTop() {
		const top = this.containerEl?.nativeElement.getBoundingClientRect().top;
		if (top) {
			this.chatDockTop = top;
		}
	}

	navigationMouseOver() {
		setTimeout(() => {
			this.miniNavigationHovered = this.navigationModeMini;
			if (this.navigationModeMini) {
				this.navStateService.setNavState(true);
			}
		});
	}

	navigationMouseLeave() {
		setTimeout(() => {
			this.miniNavigationHovered = false;
			if (this.navigationModeMini) {
				this.navStateService.setNavState(false);
			}
		});
	}

	getUserInitials(): string {
		return this.userService.getUserInitials(this.me);
	}

	dashEnable() {
		if (this.router.url === '/dash') {
			this.dashEnabled = true;
		} else {
			this.dashEnabled = false;
		}
	}
}
